import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Loader2, AlertCircle, Globe, FileText, Save } from 'lucide-react';
import { IWritingStyleFormProps } from '@/@types/writingStyle';
import { WritingStyleTextInput } from '@/components/writing-styles/WritingStyleTextInput';
import { WritingStyleUrlExtraction } from '@/components/writing-styles/WritingStyleUrlExtraction';
import { useWritingStyleForm } from '@/hooks/useWritingStyleForm';

const WritingStyleForm = ({ initialData, onSuccess, onCancel }: IWritingStyleFormProps) => {
  const form = useWritingStyleForm({ initialData, onSuccess });

  return (
    <Card className="w-full max-w-2xl">
      <form onSubmit={form.handleSubmit}>
        <CardHeader>
          <h2 className="text-2xl font-bold">
            {initialData ? 'Edit Writing Style' : 'Create Writing Style'}
          </h2>
          <p className="text-sm text-muted-foreground">
            Analyze a text sample to extract its unique writing style characteristics
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Style Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="e.g., Professional Blog, Casual Social Media"
              value={form.formData.name}
              onChange={form.handleNameChange}
              disabled={form.isSubmitting}
              className={form.errors.name ? 'border-destructive' : ''}
            />
            {form.errors.name && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {form.errors.name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Source *</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={form.formData.inputMethod === 'text' ? 'default' : 'outline'}
                size="sm"
                onClick={() => form.handleInputMethodChange('text')}
                disabled={form.isSubmitting}
                className="flex-1"
              >
                <FileText className="h-4 w-4 mr-2" />
                Paste Text
              </Button>
              <Button
                type="button"
                variant={form.formData.inputMethod === 'url' ? 'default' : 'outline'}
                size="sm"
                onClick={() => form.handleInputMethodChange('url')}
                disabled={form.isSubmitting}
                className="flex-1"
              >
                <Globe className="h-4 w-4 mr-2" />
                Extract from URL
              </Button>
            </div>
          </div>

          {form.formData.inputMethod === 'text' && (
            <WritingStyleTextInput
              sampleText={form.formData.sampleText}
              onChange={form.handleSampleTextChange}
              isSubmitting={form.isSubmitting}
              error={form.errors.sampleText}
              wordCount={form.textWordCount}
            />
          )}

          {form.formData.inputMethod === 'url' && (
            <WritingStyleUrlExtraction
              sampleUrl={form.formData.sampleUrl}
              onUrlChange={form.handleUrlChange}
              extraction={form.extraction}
              onExtractText={form.handleExtractText}
              onExtractedTextChange={form.handleExtractedTextChange}
              isEditingExtractedText={form.isEditingExtractedText}
              onToggleEditExtractedText={() =>
                form.setIsEditingExtractedText(!form.isEditingExtractedText)
              }
              isSubmitting={form.isSubmitting}
              error={form.errors.sampleUrl}
              extractedWordCount={form.extractedWordCount}
            />
          )}

          {form.errors.general && (
            <div className="bg-destructive/10 text-destructive p-3 rounded flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{form.errors.general}</span>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex gap-2 justify-end border-t pt-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={form.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              form.isSubmitting ||
              (form.formData.inputMethod === 'url' && form.extraction.status === 'extracting') ||
              (form.formData.inputMethod === 'url' && form.extraction.status === 'idle') ||
              form.currentWordCount < 300
            }
          >
            {form.isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {form.isEditMode ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                {form.isEditMode ? (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                ) : (
                  'Create & Analyze'
                )}
              </>
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default WritingStyleForm;
